// src/components/artwork/ArtworkPlacement.jsx
//
// MVP « Lieux & conservation » (doc §2.B, §9) — placement courant + MODE de présence
// + historique, par œuvre. Couche riche par-dessus artworks.location_id (write-through
// via la RPC set_artwork_placement). Le lieu « où » reste géré par l'onglet ; ici on
// documente « comment » l'œuvre est conservée, la zone, et on garde l'historique.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";
import { MapPin, History, ImagePlus, Lock, Smartphone } from "lucide-react";
import { PRESENCE_MODES, presenceModeLabel } from "../../utils/placements";
import UseMyPhoneModal from "./modals/UseMyPhoneModal";

const UI = {
  title: { fr: "Emplacement & mode de présence", en: "Placement & presence mode", de: "Platzierung & Präsenzmodus" },
  current: { fr: "Emplacement actuel", en: "Current placement", de: "Aktuelle Platzierung" },
  since: { fr: "depuis le", en: "since", de: "seit dem" },
  none: { fr: "Non renseigné", en: "Not specified", de: "Nicht angegeben" },
  update: { fr: "Mettre à jour", en: "Update", de: "Aktualisieren" },
  location: { fr: "Lieu", en: "Location", de: "Ort" },
  selectLocation: { fr: "— Choisir un lieu —", en: "— Choose a location —", de: "— Ort wählen —" },
  mode: { fr: "Mode de présence", en: "Presence mode", de: "Präsenzmodus" },
  selectMode: { fr: "— Mode de présence —", en: "— Presence mode —", de: "— Präsenzmodus —" },
  zone: { fr: "Zone / précision", en: "Zone / detail", de: "Zone / Detail" },
  zonePlaceholder: { fr: "Mur du salon, étagère B, box 12…", en: "Living-room wall, shelf B, unit 12…", de: "Wohnzimmerwand, Regal B, Box 12…" },
  packing: { fr: "Emballage / support", en: "Packing / support", de: "Verpackung / Träger" },
  save: { fr: "Enregistrer", en: "Save", de: "Speichern" },
  saving: { fr: "Enregistrement…", en: "Saving…", de: "Wird gespeichert…" },
  cancel: { fr: "Annuler", en: "Cancel", de: "Abbrechen" },
  history: { fr: "Historique des emplacements", en: "Placement history", de: "Platzierungsverlauf" },
  noHistory: { fr: "Aucun changement enregistré pour l'instant.", en: "No change recorded yet.", de: "Noch keine Änderung erfasst." },
  error: { fr: "Échec de l'enregistrement.", en: "Save failed.", de: "Speichern fehlgeschlagen." },
  photoTitle: { fr: "Photo de contexte", en: "Context photo", de: "Kontextfoto" },
  photoPrivate: { fr: "privée", en: "private", de: "privat" },
  photoHint: { fr: "L'œuvre dans son environnement, pour suivre son état dans le temps.", en: "The artwork in its setting, to track its condition over time.", de: "Das Werk in seiner Umgebung, um den Zustand über die Zeit zu verfolgen." },
  photoAdd: { fr: "Ajouter une photo", en: "Add a photo", de: "Foto hinzufügen" },
  photoReplace: { fr: "Remplacer", en: "Replace", de: "Ersetzen" },
  photoRemove: { fr: "Retirer", en: "Remove", de: "Entfernen" },
  photoUploading: { fr: "Envoi…", en: "Uploading…", de: "Wird hochgeladen…" },
  photoPhone: { fr: "Prendre avec mon téléphone", en: "Take with my phone", de: "Mit meinem Handy aufnehmen" },
};

const PLACEMENT_BUCKET = "placement-photos";

const CARD = { background: "#fffdf7", border: "1px solid #eadfc4", borderRadius: 16, padding: 20, marginTop: 24 };
const FIELD_LABEL = { display: "block", fontSize: 12, fontWeight: 600, color: "#6b6459", marginBottom: 4 };
const INPUT = { width: "100%", border: "1px solid #eadfc4", borderRadius: 10, padding: "8px 10px", fontSize: 13, background: "#fff" };

export default function ArtworkPlacement({ artworkId, clientId = null, locations = [], currentLocationId = null, onChanged, readOnly = false }) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || "fr";
  const tx = (o) => (o ? o[String(lang).slice(0, 2)] || o.fr : "");

  const [placements, setPlacements] = useState([]);
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [uid, setUid] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null); // URL signée (privée)
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({ location_id: "", mode: "", zone: "", packing: "" });

  const load = useCallback(async () => {
    if (!artworkId) return;
    const { data } = await supabase
      .from("artwork_placements")
      .select("*")
      .eq("artwork_id", artworkId)
      .order("started_on", { ascending: false });
    setPlacements(data || []);
  }, [artworkId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUid(data?.user?.id || clientId || null));
  }, [clientId]);

  const current = placements.find((p) => !p.ended_on) || null;

  // Photo de contexte privée → URL signée courte durée pour l'aperçu.
  useEffect(() => {
    let ignore = false;
    const path = current?.context_photo_url;
    if (!path) { setPhotoUrl(null); return; }
    supabase.storage.from(PLACEMENT_BUCKET).createSignedUrl(path, 3600).then(({ data }) => {
      if (!ignore) setPhotoUrl(data?.signedUrl || null);
    });
    return () => { ignore = true; };
  }, [current?.context_photo_url]);

  // S'assure qu'un placement ouvert existe (sinon en crée un) et renvoie son id.
  const ensurePlacementId = async () => {
    if (current?.id) return current.id;
    const { data, error } = await supabase.rpc("set_artwork_placement", {
      p_artwork_id: artworkId,
      p_location_id: currentLocationId || null,
      p_mode: null, p_zone: null, p_packing: null,
    });
    if (error) return null;
    await load();
    return data || null;
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file || uploadingPhoto) return;
    const owner = uid || clientId;
    if (!owner) { setErr(tx(UI.error)); return; }
    setUploadingPhoto(true);
    setErr("");
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `${owner}/${artworkId}/ctx_${Date.now()}.${ext}`;
      const up = await supabase.storage.from(PLACEMENT_BUCKET).upload(path, file, { upsert: true, contentType: file.type });
      if (up.error) throw up.error;
      const pid = await ensurePlacementId();
      if (!pid) throw new Error("no_placement");
      const { error } = await supabase
        .from("artwork_placements")
        .update({ context_photo_url: path, updated_at: new Date().toISOString() })
        .eq("id", pid);
      if (error) throw error;
      await load();
    } catch {
      setErr(tx(UI.error));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    if (!current?.id) return;
    const path = current.context_photo_url;
    await supabase.from("artwork_placements").update({ context_photo_url: null, updated_at: new Date().toISOString() }).eq("id", current.id);
    if (path) supabase.storage.from(PLACEMENT_BUCKET).remove([path]);
    setPhotoUrl(null);
    await load();
  };

  // Retour de la passerelle QR (photo prise au téléphone) : rattache le chemin privé.
  const handlePhoneCompleted = async (row) => {
    setPhoneOpen(false);
    const path = row?.result_path;
    if (!path) return;
    setUploadingPhoto(true);
    try {
      const pid = await ensurePlacementId();
      if (pid) {
        await supabase.from("artwork_placements")
          .update({ context_photo_url: path, updated_at: new Date().toISOString() })
          .eq("id", pid);
        await load();
      }
    } finally {
      setUploadingPhoto(false);
    }
  };
  const history = placements.filter((p) => p.ended_on);
  const locName = (id) => locations.find((l) => l.id === id)?.name || null;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(lang, { day: "numeric", month: "short", year: "numeric" }) : "—");

  const startEdit = () => {
    setForm({
      location_id: current?.location_id || currentLocationId || "",
      mode: current?.mode || "",
      zone: current?.zone || "",
      packing: current?.packing || "",
    });
    setErr("");
    setEditing(true);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setErr("");
    const { error } = await supabase.rpc("set_artwork_placement", {
      p_artwork_id: artworkId,
      p_location_id: form.location_id || null,
      p_mode: form.mode || null,
      p_zone: form.zone || null,
      p_packing: form.packing || null,
    });
    setSaving(false);
    if (error) { setErr(tx(UI.error)); return; }
    setEditing(false);
    await load();
    if (onChanged) onChanged(form.location_id || null);
  };

  return (
    <section style={CARD}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "Georgia, serif", fontSize: 18, margin: 0, color: "#1a1a1a" }}>
          <MapPin size={17} strokeWidth={1.8} style={{ color: "#a9843f" }} aria-hidden="true" />
          {tx(UI.title)}
        </h3>
        {!readOnly && !editing && (
          <button
            type="button"
            onClick={startEdit}
            style={{ fontSize: 13, fontWeight: 600, color: "#a9843f", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            {tx(UI.update)}
          </button>
        )}
      </div>

      {/* Placement courant */}
      {!editing && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "#a9843f", fontWeight: 700 }}>
            {tx(UI.current)}
          </div>
          <div style={{ fontSize: 14.5, color: "#1a1a1a", marginTop: 4 }}>
            {(current && (locName(current.location_id) || tx(UI.none)))
              || locName(currentLocationId) || tx(UI.none)}
            {current?.mode && <> · <span style={{ color: "#6b6459" }}>{presenceModeLabel(current.mode, lang)}</span></>}
          </div>
          {current?.zone && <div style={{ fontSize: 12.5, color: "#8a8378", marginTop: 2 }}>{current.zone}</div>}
          {current?.started_on && (
            <div style={{ fontSize: 12, color: "#a49b8b", marginTop: 4 }}>{tx(UI.since)} {fmt(current.started_on)}</div>
          )}

          {/* Photo de contexte privée (doc §8.5) — masquée en lecture seule s'il n'y en a pas */}
          {(photoUrl || !readOnly) && (
          <div style={{ marginTop: 14, borderTop: "1px solid #f0e8d6", paddingTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#1a1a1a" }}>{tx(UI.photoTitle)}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, color: "#8a6d2f", background: "#faf3e2", border: "1px solid #ecdcb8", borderRadius: 999, padding: "1px 7px" }}>
                <Lock size={10} aria-hidden="true" /> {tx(UI.photoPrivate)}
              </span>
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={uploadPhoto} />
            {photoUrl ? (
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <img src={photoUrl} alt="" style={{ width: 120, height: 90, objectFit: "cover", borderRadius: 10, border: "1px solid #eadfc4" }} />
                {!readOnly && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadingPhoto} style={{ fontSize: 12.5, fontWeight: 600, color: "#a9843f", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                      {uploadingPhoto ? tx(UI.photoUploading) : tx(UI.photoReplace)}
                    </button>
                    <button type="button" onClick={() => setPhoneOpen(true)} disabled={uploadingPhoto} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: "#6b6459", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                      <Smartphone size={13} aria-hidden="true" /> {tx(UI.photoPhone)}
                    </button>
                    <button type="button" onClick={removePhoto} style={{ fontSize: 12.5, color: "#b3261e", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                      {tx(UI.photoRemove)}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadingPhoto}
                    style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#6b6459", background: "#fff", border: "1px dashed #d9cba7", borderRadius: 10, padding: "9px 14px", cursor: uploadingPhoto ? "default" : "pointer" }}
                  >
                    <ImagePlus size={15} aria-hidden="true" /> {uploadingPhoto ? tx(UI.photoUploading) : tx(UI.photoAdd)}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPhoneOpen(true)}
                    disabled={uploadingPhoto}
                    style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#6b6459", background: "#fff", border: "1px dashed #d9cba7", borderRadius: 10, padding: "9px 14px", cursor: "pointer" }}
                  >
                    <Smartphone size={15} aria-hidden="true" /> {tx(UI.photoPhone)}
                  </button>
                </div>
                <div style={{ fontSize: 11.5, color: "#a49b8b", marginTop: 6 }}>{tx(UI.photoHint)}</div>
              </div>
            )}
            {err && <div style={{ fontSize: 12, color: "#b3261e", marginTop: 6 }}>{err}</div>}
          </div>
          )}
        </div>
      )}

      {/* Formulaire de mise à jour */}
      {editing && (
        <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={FIELD_LABEL}>{tx(UI.location)}</label>
              <select style={INPUT} value={form.location_id} onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))}>
                <option value="">{tx(UI.selectLocation)}</option>
                {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
              </select>
            </div>
            <div>
              <label style={FIELD_LABEL}>{tx(UI.mode)}</label>
              <select style={INPUT} value={form.mode} onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}>
                <option value="">{tx(UI.selectMode)}</option>
                {PRESENCE_MODES.map((m) => (<option key={m.key} value={m.key}>{presenceModeLabel(m.key, lang)}</option>))}
              </select>
            </div>
          </div>
          <div>
            <label style={FIELD_LABEL}>{tx(UI.zone)}</label>
            <input style={INPUT} value={form.zone} onChange={(e) => setForm((f) => ({ ...f, zone: e.target.value }))} placeholder={tx(UI.zonePlaceholder)} />
          </div>
          <div>
            <label style={FIELD_LABEL}>{tx(UI.packing)}</label>
            <input style={INPUT} value={form.packing} onChange={(e) => setForm((f) => ({ ...f, packing: e.target.value }))} />
          </div>
          {err && <div style={{ fontSize: 12.5, color: "#b3261e" }}>{err}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{ background: "#1a1a1a", color: "#fff", fontSize: 13, fontWeight: 500, padding: "10px 18px", borderRadius: 12, border: "none", cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1 }}
            >
              {saving ? tx(UI.saving) : tx(UI.save)}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              style={{ background: "#fff", color: "#6b6459", fontSize: 13, padding: "10px 16px", borderRadius: 12, border: "1px solid #eadfc4", cursor: "pointer" }}
            >
              {tx(UI.cancel)}
            </button>
          </div>
        </div>
      )}

      {/* Historique */}
      {!editing && (
        <div style={{ marginTop: 14, borderTop: "1px solid #f0e8d6", paddingTop: 12 }}>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#6b6459", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <History size={14} aria-hidden="true" /> {tx(UI.history)} {history.length > 0 ? `(${history.length})` : ""}
          </button>
          {showHistory && (
            <div style={{ marginTop: 10 }}>
              {history.length === 0 ? (
                <div style={{ fontSize: 12.5, color: "#a49b8b" }}>{tx(UI.noHistory)}</div>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
                  {history.map((p) => (
                    <li key={p.id} style={{ fontSize: 12.5, color: "#6b6459", display: "flex", gap: 8 }}>
                      <span style={{ color: "#c9a961" }}>•</span>
                      <span>
                        <strong style={{ color: "#1a1a1a", fontWeight: 600 }}>{locName(p.location_id) || tx(UI.none)}</strong>
                        {p.mode && <> · {presenceModeLabel(p.mode, lang)}</>}
                        {" — "}{fmt(p.started_on)} → {fmt(p.ended_on)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Passerelle QR desktop → téléphone pour la photo de contexte privée */}
      <UseMyPhoneModal
        open={phoneOpen}
        onClose={() => setPhoneOpen(false)}
        artworkId={artworkId}
        actionType="placement_context"
        onCompleted={handlePhoneCompleted}
      />
    </section>
  );
}
